import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { EMPTY, MonoTypeOperatorFunction, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

type RedirectTarget = '_self' | '_blank';

function handleRedirect<T>(): MonoTypeOperatorFunction<T> {
  return catchError((error: HttpErrorResponse) => {
    // responseType 'blob' skips Angular's JSON parsing, so the payload arrives as undecoded bytes
    if (!(error.error instanceof Blob)) {
      return redirectOrThrow(error, error.error, '_self');
    }

    // a blob request is a file download, so its redirect points at a document, not at an app page
    return from(error.error.text()).pipe(switchMap((text) => redirectOrThrow(error, parseJson(text), '_blank')));
  });
}

function redirectOrThrow(error: HttpErrorResponse, body: any, target: RedirectTarget): Observable<never> {
  const redirectUrl = body?.detail;

  if (body?.typeCode && Number(body.typeCode) === ErrorCode.Redirect && error.status === 400 && redirectUrl) {
    if (target === '_blank') {
      window.open(redirectUrl, '_blank');
    } else {
      window.location.href = redirectUrl;
    }

    return EMPTY;
  }

  return throwError(() => error);
}

function parseJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const redirectInterceptor: HttpInterceptorFn = (req, next) => next(req).pipe(handleRedirect());

@Injectable()
export class RedirectInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(handleRedirect());
  }
}
